import React from 'react';

import { CheckboxWithLabel } from '@depop/web-ui-kit/CheckboxWithLabel';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';

import { MobileFilterFooter } from '../MobileFilterFooter';
import { MobileFilterHeader } from '../MobileFilterHeader';
import { SearchFilterTrigger } from '../SearchFilterTrigger';
import { CollapsibleRow } from '../SizeFilter/CollapsibleRow';
import baseFilterStyles from '../baseFilterStyles.module.css';

import styles from './styles.module.css';

import { Dropdown } from '@/components/Dropdown';
import { ActivityTrackerEventType } from '@/modules/activityTracker/constants';
import { useActivityTracker } from '@/modules/activityTracker/useActivityTracker';
import { useFilterByCategoryOptions } from '@/modules/categories/hooks/useFilterByCategoryOptions';
import {
  getCompositeValue,
  getDepartmentId,
} from '@/modules/drafts/helpers/categories';
import { FilterTypes } from '@/modules/filters/types';
import { useSearchFilters } from '@/modules/search/useSearchFilters';
import { useCurrentUser } from '@/modules/user/useCurrentUser';

type Props = {
  closeMobileFilterDrawer: () => void;
  sellerId?: number;
};

export function CategoryFilter({ closeMobileFilterDrawer, sellerId }: Props) {
  const t = useTranslations('common');
  const { searchFilters, setSearchFilters } = useSearchFilters();
  const [, user] = useCurrentUser();
  const { sendActivityTrackerEvent } = useActivityTracker({ userId: user?.id });
  const department = getDepartmentId(
    searchFilters.gender,
    searchFilters.isKids
  );
  const initialValue = getCompositeValue(department, searchFilters.groups);

  const title = t('Category');

  const categoryFilters = useFilterByCategoryOptions(sellerId);

  return (
    <Dropdown
      TriggerComponent={(props) => (
        <SearchFilterTrigger
          {...props}
          isActive={Boolean(searchFilters.groups)}
          title={title}
        />
      )}
      onOpen={() =>
        sendActivityTrackerEvent(
          ActivityTrackerEventType.FILTER_OPENED_ACTION,
          { filterType: FilterTypes.Category }
        )
      }
    >
      {({ className, toggleDropdown }) => {
        return (
          <div
            className={clsx(
              className,
              baseFilterStyles.searchFilterDropdownContents,
              styles.categoryFilterDropdownContents
            )}
          >
            <MobileFilterHeader
              title={title}
              toggleDropdown={toggleDropdown}
              closeMobileFilterDrawer={closeMobileFilterDrawer}
            />
            <div className={baseFilterStyles.content}>
              {categoryFilters.map(
                ({ name: departmentName, id: department, children }) => {
                  return (
                    <CollapsibleRow
                      key={`accordion-${department}`}
                      label={departmentName}
                    >
                      {children?.map(({ id: group, name }) => (
                        <CheckboxWithLabel
                          className={clsx(
                            baseFilterStyles.searchFilterCheckbox,
                            styles.categoryCheckbox
                          )}
                          key={name}
                          label={name}
                          align="left"
                          value={getCompositeValue(department, group)}
                          onChange={() =>
                            setSearchFilters.setGroupsFilter(department, group)
                          }
                          checked={
                            initialValue ===
                            getCompositeValue(department, group)
                          }
                        />
                      ))}
                    </CollapsibleRow>
                  );
                }
              )}
            </div>
            <MobileFilterFooter
              handleSubmit={closeMobileFilterDrawer}
              handleClear={() => setSearchFilters.setGroupsFilter()}
              sellerId={sellerId}
            />
          </div>
        );
      }}
    </Dropdown>
  );
}
